import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const STORAGE_KEY = "hm-consent";
const VERSION = 1; // bump when categories or their meaning change → banner shows again
export const CATEGORIES = ["necessary", "media"];

const read = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (data.version !== VERSION || typeof data.choices !== "object") return null;
    return data;
  } catch {
    return null;
  }
};

const write = (choices) => {
  const data = { version: VERSION, at: new Date().toISOString(), choices: { ...choices, necessary: true } };
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* storage blocked – choice lives for this session only */
  }
  return data;
};

const ConsentContext = createContext(null);

export const ConsentProvider = ({ children }) => {
  const [stored, setStored] = useState(read);
  const [open, setOpen] = useState(() => read() === null);
  const [settingsView, setSettingsView] = useState(false);

  const choices = useMemo(() => stored?.choices || { necessary: true, media: false }, [stored]);

  const decide = useCallback((next) => {
    setStored(write(next));
    setOpen(false);
    setSettingsView(false);
  }, []);

  const acceptAll = useCallback(() => decide(Object.fromEntries(CATEGORIES.map((c) => [c, true]))), [decide]);
  const rejectAll = useCallback(() => decide({ necessary: true, media: false }), [decide]);
  const openSettings = useCallback(() => {
    setSettingsView(true);
    setOpen(true);
  }, []);

  // Let other tabs / the legal page react to changes.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setStored(read());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(
    () => ({
      choices,
      decided: stored !== null,
      has: (cat) => Boolean(choices[cat]),
      acceptAll,
      rejectAll,
      decide,
      open,
      settingsView,
      setSettingsView,
      openSettings,
      close: () => setOpen(false),
    }),
    [choices, stored, acceptAll, rejectAll, decide, open, settingsView, openSettings]
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useConsent must be used within ConsentProvider");
  return ctx;
};
